import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { APP_VERSION } from '../../config/version';

/**
 * App-wide footer: copyright + build version (matches the repo VERSION file).
 *
 * CHANGED in 1.17.0-dev.1 — colours and hairline moved onto the design system
 * tokens; the type is uppercase 13px, which is how the site sets its copyright
 * strip. Structure and behaviour untouched.
 */
const Footer = styled.footer`
  padding:${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  text-align:center;
  font-size:${({ theme }) => theme.typography.fontSize.xs};
  font-weight:${({ theme }) => theme.typography.fontWeight.regular};
  letter-spacing:${({ theme }) => theme.typography.letterSpacing.wide};
  text-transform:uppercase;
  color:${({ theme }) => theme.colors.textSecondary};
  background-color:transparent;
  border-top:1px solid ${({ theme }) => theme.colors.hairline};
  display:flex;
  justify-content:center;
  align-items:center;
  gap:${({ theme }) => theme.spacing[3]};
  flex-wrap:wrap;
`;

const Version = styled.span`
  font-variant-numeric:tabular-nums;
  color:${({ theme }) => theme.colors.textTertiary};
`;

/* The footer is rendered by every layout INCLUDING AuthLayout, so these two
   links are what makes the privacy policy and the licence terms reachable
   before sign-in — which is what GDPR art. 12-13 and the incorporation of
   standard terms require. Plain <a> in a new tab, like the user-guide link:
   a legal document should never cost the reader their place in the app. */
const LegalLink = styled.a`
  color:${({ theme }) => theme.colors.textSecondary};
  text-decoration:none;

  &:hover { text-decoration:underline; }
`;

const AppFooter = () => {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  return (
    <Footer>
      <span>{t('footer.rights', { year })}</span>
      <LegalLink href="/legal/privatlivspolitik" target="_blank" rel="noopener noreferrer">
        {t('legal.privacy')}
      </LegalLink>
      <LegalLink href="/legal/slutbrugerlicensaftale" target="_blank" rel="noopener noreferrer">
        {t('legal.terms')}
      </LegalLink>
      <Version aria-label={t('footer.version', { version: APP_VERSION })}>
        v{APP_VERSION}
      </Version>
    </Footer>
  );
};

export default AppFooter;
